import { useMemo, useState, type KeyboardEvent } from 'react';
import { SearchInput } from '@/components/ui';

export interface UnifiedSkillFacets {
  verticals: string[];
  functions: string[];
  tags: string[];
}

interface Suggestion {
  token: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  facets: UnifiedSkillFacets;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

function buildMixedSuggestions(facets: UnifiedSkillFacets, query: string): Suggestion[] {
  const q = query.toLowerCase();
  const out: Suggestion[] = [];

  facets.verticals
    .filter((v) => v.toLowerCase().includes(q))
    .slice(0, 4)
    .forEach((v) => out.push({ token: `vertical:${v}`, label: `Vertical: ${v}` }));

  facets.functions
    .filter((f) => f.toLowerCase().includes(q))
    .slice(0, 4)
    .forEach((f) => out.push({ token: `function:${f}`, label: `Function: ${f}` }));

  facets.tags
    .filter((t) => t.toLowerCase().includes(q))
    .slice(0, 8)
    .forEach((t) => out.push({ token: `tag:${t}`, label: `Tag: ${t}` }));

  return out.slice(0, 12);
}

function replaceLastToken(input: string, nextToken: string): string {
  if (!input.trim()) return `${nextToken} `;
  if (/\s$/.test(input)) return `${input}${nextToken} `;
  return `${input.replace(/\S+$/, '')}${nextToken} `;
}

export function UnifiedSkillSearchInput({
  value,
  onChange,
  facets,
  placeholder = 'Search by text or use tokens: vertical:, function:, tag:, -tag:',
  className,
  autoFocus = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const currentToken = useMemo(() => {
    const parts = value.split(/\s+/);
    return parts[parts.length - 1]?.trim() ?? '';
  }, [value]);

  const suggestions = useMemo(() => {
    const token = currentToken.toLowerCase();
    if (!token) return [];

    if (token.startsWith('vertical:') || token.startsWith('v:')) {
      const q = token.split(':').slice(1).join(':');
      return facets.verticals
        .filter((v) => v.toLowerCase().includes(q))
        .slice(0, 12)
        .map((v) => ({ token: `vertical:${v}`, label: `Vertical: ${v}` }));
    }

    if (token.startsWith('function:') || token.startsWith('f:')) {
      const q = token.split(':').slice(1).join(':');
      return facets.functions
        .filter((f) => f.toLowerCase().includes(q))
        .slice(0, 12)
        .map((f) => ({ token: `function:${f}`, label: `Function: ${f}` }));
    }

    if (token.startsWith('tag:') || token.startsWith('-tag:')) {
      const negative = token.startsWith('-tag:');
      const q = token.split(':').slice(1).join(':');
      return facets.tags
        .filter((t) => t.toLowerCase().includes(q))
        .slice(0, 12)
        .map((t) => ({ token: `${negative ? '-tag' : 'tag'}:${t}`, label: `Tag: ${t}` }));
    }

    return buildMixedSuggestions(facets, token);
  }, [currentToken, facets]);

  function applySuggestion(suggestion: Suggestion) {
    onChange(replaceLastToken(value, suggestion.token));
    setOpen(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((idx) => Math.min(idx + 1, suggestions.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((idx) => Math.max(idx - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      applySuggestion(suggestions[activeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="relative">
      <SearchInput
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
      />

      {open && suggestions.length > 0 && (
        <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-md border border-border bg-popover shadow-lg">
          <div className="max-h-64 overflow-auto p-1">
            {suggestions.map((suggestion, index) => {
              const active = index === activeIndex;
              return (
                <button
                  key={`${suggestion.token}-${index}`}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => applySuggestion(suggestion)}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm ${
                    active ? 'bg-accent' : 'hover:bg-accent/70'
                  }`}
                >
                  {suggestion.label}
                </button>
              );
            })}
          </div>
          <div className="border-t px-3 py-2 text-xs text-muted-foreground">
            Tokens: vertical:, function:, tag:, -tag:
          </div>
        </div>
      )}
    </div>
  );
}
